import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AuthSession,
  CognitoTokenClient,
  AmsExchangeClient,
  RenewalPolicy,
  tokenStore,
  cookieStore,
  verifierStore,
  createLogoutBroadcastChannel,
  signInRequired,
  CALLBACK_PATH,
  callbackCarriesPayload,
  type AuthConfig,
  type AuthSessionLike,
  type SessionOutcome,
} from '@bettercomp/ams-spa-auth';

export interface UseAmsAuthSessionOptions {
  /**
   * Session endpoints and client id. Must be referentially stable across renders
   * (memoize it from the config fetch) — a new identity tears the session down
   * and constructs a fresh one.
   */
  config: AuthConfig | undefined;
  /** False stands the session machinery down entirely (e.g. a dev-identity bypass). */
  enabled: boolean;
  /**
   * Bootstrap even when the URL is a payload-carrying login callback. Default
   * false: a host that renders CallbackView must leave that exchange to it — a
   * parallel bootstrap would race the one-shot code exchange. A host that
   * completes login by other means and arms the session afterwards (e.g. a
   * legacy-first callback that only seeds the refresh cookie) sets true so the
   * session mints itself without a manual bootstrap call.
   */
  bootstrapOnCallback?: boolean;
  /** Called after a signInRequired-triggered renewal actually renews (e.g. to refetch failed queries). */
  onRenewed?: () => void;
  /**
   * Called with the outcome each bootstrap resolves to — the initial bootstrap
   * and every retryBootstrap. Renewals and cross-tab sign-outs, which the
   * session publishes into `outcome`, never reach it, so a host can react to
   * "the session was just established" without filtering the outcome stream
   * (e.g. load the current user once, or report a failed first mint).
   */
  onBootstrapped?: (outcome: SessionOutcome) => void;
}

/**
 * bootstrap() resolves to a SessionOutcome for every failure it anticipates, but an
 * unguarded storage access inside it can still throw (cookie access blocked by the
 * browser). A rejection would reach neither `outcome` nor `onBootstrapped` and leave
 * the gate loading forever, so it is settled into the outcome the core itself uses
 * for that condition. The thrown error is logged because the outcome carries no detail.
 */
const settleBootstrap = (bootstrap: Promise<SessionOutcome>): Promise<SessionOutcome> =>
  bootstrap.catch((error: unknown): SessionOutcome => {
    console.error('AMS session bootstrap threw instead of resolving to an outcome', error);
    return { kind: 'unavailable', source: 'storage', code: 'inaccessible' };
  });

export interface UseAmsAuthSessionResult {
  session: AuthSessionLike | undefined;
  outcome: SessionOutcome | undefined;
  /**
   * Publishes an outcome the host established itself into the gate: to re-seat
   * the session after a host-driven re-bootstrap (`reportOutcome(await
   * session.bootstrap())`), or to report a `host`-sourced outcome when the
   * host's own post-mint identity load fails. A host-reported outcome is a
   * statement about the host's identity, not about the session: the session
   * keeps its renewal timer and republishes `minted` on each renewal (about
   * every 15 minutes), which overwrites the host outcome. Hosts re-run their
   * identity load when the gate reopens and report again if it still fails;
   * the package does not suppress renewals for them.
   */
  reportOutcome: (outcome: SessionOutcome) => void;
  /** Re-runs session.bootstrap — the retry affordance for unavailable outcomes. */
  retryBootstrap: () => void;
}

/**
 * Owns the shared lifecycle of an AMS auth session inside a React app: constructs
 * the session graph once config is available, bootstraps (unless the URL is a
 * callback carrying a login payload — that exchange belongs to the callback view),
 * relays published outcomes into state, renews on the signInRequired signal, and
 * unsubscribes/disposes on teardown. Config fetching, dev-identity bypass, and
 * app-specific reactions to outcomes stay with the caller.
 */
export function useAmsAuthSession({
  config,
  enabled,
  bootstrapOnCallback = false,
  onRenewed,
  onBootstrapped,
}: UseAmsAuthSessionOptions): UseAmsAuthSessionResult {
  const [session, setSession] = useState<AuthSessionLike>();
  const [outcome, setOutcome] = useState<SessionOutcome>();

  // AuthSession's internal generation guard covers its side effects, not the value its
  // bootstrap promise resolves with — a resolution that lands after the session was torn
  // down (StrictMode remount, config identity change) must not overwrite the live
  // session's outcome. The ref tracks which session is current; late resolutions no-op.
  const activeSessionRef = useRef<AuthSessionLike | undefined>(undefined);

  const onRenewedRef = useRef(onRenewed);
  const onBootstrappedRef = useRef(onBootstrapped);
  useEffect(() => {
    onRenewedRef.current = onRenewed;
    onBootstrappedRef.current = onBootstrapped;
  });

  useEffect(() => {
    if (!enabled || !config) {
      activeSessionRef.current = undefined;
      setSession(undefined);
      setOutcome(undefined);
      return;
    }
    const newSession = new AuthSession({
      config,
      cognitoClient: new CognitoTokenClient({ hostedUiUrl: config.hostedUiUrl, clientId: config.clientId }),
      amsExchangeClient: new AmsExchangeClient({ exchangeUrl: config.exchangeUrl }),
      tokenStore,
      cookieStore,
      verifierStore,
      renewalPolicy: new RenewalPolicy(),
      channelFactory: createLogoutBroadcastChannel,
      origin: window.location.origin,
    });
    activeSessionRef.current = newSession;
    setSession(newSession);
    const setOutcomeIfCurrent = (bootstrapOutcome: SessionOutcome) => {
      if (activeSessionRef.current === newSession) {
        setOutcome(bootstrapOutcome);
        onBootstrappedRef.current?.(bootstrapOutcome);
      }
    };
    if (bootstrapOnCallback || !(window.location.pathname === CALLBACK_PATH && callbackCarriesPayload(window.location.search))) {
      void settleBootstrap(newSession.bootstrap()).then(setOutcomeIfCurrent);
    }
    const unsubscribe = newSession.subscribe(setOutcome);
    return () => {
      if (activeSessionRef.current === newSession) {
        activeSessionRef.current = undefined;
      }
      unsubscribe();
      newSession.dispose();
    };
  }, [enabled, config, bootstrapOnCallback]);

  useEffect(() => {
    if (!session) {
      return;
    }
    return signInRequired.subscribe(() => {
      void session.renew({ trigger: 'unauthorized' }).then((result) => {
        if (result.status === 'renewed') {
          onRenewedRef.current?.();
        }
      });
    });
  }, [session]);

  const retryBootstrap = useCallback(() => {
    const current = activeSessionRef.current;
    if (!current) {
      return;
    }
    void settleBootstrap(current.bootstrap()).then((bootstrapOutcome) => {
      if (activeSessionRef.current === current) {
        setOutcome(bootstrapOutcome);
        onBootstrappedRef.current?.(bootstrapOutcome);
      }
    });
  }, []);

  return { session, outcome, reportOutcome: setOutcome, retryBootstrap };
}
